import React, { useEffect, useContext, useState } from 'react'
import PropertyGrid from '../../../../components/PropertyGrid'
import { AppContext } from '../../../../global'
import * as actions from '../store/actions'
import './CertificateSettings.css'
import Date from '../../../../components/Date'
import { useTranslation } from 'react-i18next'
import AddCertificate from './AddCertificate'
import { Certificate } from '../store/store'
import RequestStateContainer from '../../../../components/containers/RequestStateContainer'
import EmptyList from '../../../../components/EmptyList'
import { ButtonSecondary } from '../../../../components/atoms'
import { RequestState } from '../../../types'

type Props = {
  accountId: string
}

function CertificatesSettings(props: Props) {
  const { accountId } = props
  const { state, execute, confirm } = useContext(AppContext)
  const certificateState = state.settings.certificates
  const certificates = certificateState.list.certificates
  const { t } = useTranslation(['global', 'settings_certificates'])
  const [certificateModalOpen, setCertificateModalOpen] = useState(false)

  async function load() {
    await execute(actions.loadAvailableCertificates(accountId))
    await execute(actions.loadAccountCertificates(accountId))
  }

  useEffect(() => {
    load()
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId])

  function openCertificateModal() {
    setCertificateModalOpen(true)
  }

  function closeCertificateModal() {
    setCertificateModalOpen(false)
  }

  function getDeleteText(cert: Certificate) {
    return cert.deleteRequestState === RequestState.Loading
    ? t('global:deleting')
    : t('global:delete')
  }

  async function addCertificate(certificate: Certificate, label: string) {
    const existingCert = certificateState.list.certificates.find(c => c.key === certificate.key)

    if (!existingCert) {
      setCertificateModalOpen(false)
      await execute(actions.addAccountCertificate(accountId, certificate.id, label))
      await execute(actions.loadAccountCertificates(accountId))
    } else {
      alert(t('settings_certificates:certificate_exists'))
    }
  }

  async function removeCertificate(accountId: string, accountCertId: string) {
    confirm({
      title: '',
      text: t('settings_certificates:confirm_remove'),
      callback: async () => {
        await execute(actions.removeAccountCertificate(accountId, accountCertId))
        await execute(actions.loadAccountCertificates(accountId))
      }
    })
  }

  return (
    <div>
      {certificateModalOpen && (
        <AddCertificate onCancel={closeCertificateModal} onAddCertificate={addCertificate} accountId={accountId} />
      )}

      <RequestStateContainer requestState={certificateState.list.requestState} errorText={t('settings_certificates:cannot_load')}>
        {certificates.length === 0 && (
          <EmptyList message={t('settings_certificates:no_certificates')} icon="fas fa-file-alt" addButtonInfo={{
            text: t('settings_certificates:add_certificate'),
            onClick: openCertificateModal
          }} />
        )}

        {certificates.length > 0 && (
          <div>
            <div className="padding-s">
              <button id="add-certificate" onClick={openCertificateModal}>{t('settings_certificates:add_certificate')}</button>
            </div>

            <div id="certificate-container">
              <table data-cy="certificate-table" className="content-table" cellSpacing={0} cellPadding={0}>
                <thead>
                  <tr>
                    <th>{t('settings_certificates:name')}</th>
                    <th>{t('settings_certificates:key')}</th>
                    <th>{t('settings_certificates:added')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {certificates.map(cert => (
                    <tr data-cy={cert.key} key={cert.key}>
                      <td data-cy="label">{cert.label}</td>
                      <td data-cy="key">{cert.key}</td>
                      <td data-cy="added">
                        <Date date={cert.createdAt} />
                      </td>
                      <td className="align-right">
                        <ButtonSecondary
                          className="action-btn"
                          data-cy="remove-cert" 
                          disabled={cert.deleteRequestState === RequestState.Loading}
                          onClick={() => removeCertificate(accountId, cert.id)}>
                          {getDeleteText(cert)} 
                        </ButtonSecondary>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div id="certificate-container-compact">
              <div>
                {certificateState.list.certificates.map(cert => (
                  <React.Fragment key={cert.id}>
                    <div className="header-cell flex-justify-start">
                      {cert.label}
                      <i className="fas fa-trash icon-button" data-cy={`remove-cert-${cert.key}`} onClick={() => removeCertificate(accountId, cert.id)}></i>
                    </div>
                    <div data-cy={cert.key} className="padding-s">
                      <PropertyGrid properties={{
                        [t('settings_certificates:name')]: (
                          <span data-cy="label">{cert.label}</span>
                        ),
                        [t('settings_certificates:key')]: (
                          <span data-cy="key">{cert.key}</span>
                        ),
                        [t('settings_certificates:added')]: (
                          <span><Date date={cert.createdAt} /></span>
                        )
                      }} />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </RequestStateContainer>
    </div>
  )
}

export default CertificatesSettings
